import { ArrowUpwardRounded } from '@mui/icons-material';
import { Fab } from '@mui/material';
import { throttle } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { withTestId } from 'utils/utils';

type ScrollToTopButtonProps = {
  scrollContainerRef: RefObject<HTMLElement>;
  bottom?: number;
  right?: number;
  threshold?: number;
};

export const ScrollToTopButton = ({
  scrollContainerRef,
  bottom = 20,
  right = 20,
  threshold = 0
}: ScrollToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = useCallback(() => {
    setIsVisible(prevState => {
      // If it's already visible, only disable if it's 0
      if (prevState) {
        return (scrollContainerRef.current?.scrollTop || 0) > 0;
      }

      // Only enable if the scroll position is greater than the threshold
      return (
        typeof scrollContainerRef.current?.scrollTop === 'number' &&
        scrollContainerRef.current?.scrollTop > threshold
      );
    });
  }, [scrollContainerRef, threshold]);

  const throttledScrollHandler = useMemo(() => throttle(handleScroll, 100), [handleScroll]);

  // Check visibility on mount
  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', throttledScrollHandler);

    return () => {
      container?.removeEventListener('scroll', throttledScrollHandler);
      throttledScrollHandler.cancel();
    };
  }, [throttledScrollHandler, scrollContainerRef]);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <StyledScrollToTopButton
      {...withTestId('scroll-to-top-button')} 
      aria-label="Scroll to top"
      $isVisible={isVisible}
      $bottom={bottom}
      $right={right}
      onClick={scrollToTop}
    >
      <ArrowUpwardRounded />
    </StyledScrollToTopButton>
  );
};

const StyledScrollToTopButton = styled(Fab)<{
  $isVisible: boolean;
  $bottom: number;
  $right: number;
}>`
  position: fixed;
  bottom: ${props => props.$bottom}px;
  right: ${props => props.$right}px;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? theme.colors.commonBlackMain : theme.colors.commonWhiteMain};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: ${({ $isVisible }) =>
    $isVisible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(20px)'};

  &:hover {
    background-color: ${({ theme }) =>
      theme.mode === 'dark' ? theme.colors.palette.dark__800 : theme.colors.palette.dark__50};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;
